import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

// Routes grouped by section — navigating within a group keeps scroll position
const SECTION_GROUPS = {
  settings: [
    "profile", "services", "session-types", "designations", "rolelist", "addrole",
    "clinic-details", "business-hours", "appointment-settings", "tax-settings",
    "invoice-settings", "branding", "preferences", "help-support", "login-history",
    "change-password", "plan-checkout",
  ],
  billing: ["invoice", "income", "expense", "billing", "billing/pricing-plans", "billing/settlement", "billing/attendance"],
  staff: ["employeelist", "addemployee", "employeeprofile", "salary", "device-manager"],
  patients: ["allpatient", "addpatient", "patientprofile"],
  appointments: ["appointment", "waiting-list", "attendance"],
};

const getGroup = (path) => {
  const clean = path.replace(/^\/portal\/?/, "").split("?")[0];
  for (const [group, paths] of Object.entries(SECTION_GROUPS)) {
    if (paths.some((p) => clean === p || clean.startsWith(p + "/"))) return group;
  }
  return clean.split("/")[0] || "root";
};

const STORAGE_KEY = "vistaar_portal_scroll";

// Scroll offset per path, seeded from sessionStorage so a reload lands back
// where the user was reading. The browser restores its own window scroll on
// refresh, but the portal scrolls <main>, so without this every F5 jumped to
// the top of the page.
const readStored = () => {
  try {
    return JSON.parse(sessionStorage.getItem(STORAGE_KEY)) || {};
  } catch {
    return {}; // private mode / storage disabled — in-memory only
  }
};
const scrollPositions = readStored();
const persist = () => {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(scrollPositions));
  } catch { /* ignore */ }
};

// True while a restore is in flight, so the scroll listener does not overwrite
// the saved offset with the clamped values seen on the way back down.
let restoring = false;

// A portal page mounts short and grows as its data arrives, so one scrollTop
// assignment just clamps to whatever height exists at that instant. Keep
// re-applying until the content is tall enough to hold the offset — and stand
// down the moment the user scrolls themselves, rather than yanking the page
// out from under them.
const restoreScroll = (main, target) => {
  const deadline = Date.now() + 3000;
  const events = ["wheel", "touchstart", "keydown"];
  let done = false;
  const finish = () => {
    done = true;
    restoring = false;
    events.forEach((e) => window.removeEventListener(e, finish));
  };
  events.forEach((e) => window.addEventListener(e, finish, { passive: true }));

  restoring = true;
  const tick = () => {
    if (done) return;
    main.scrollTop = target;
    if (Math.abs(main.scrollTop - target) < 2 || Date.now() > deadline) {
      finish();
      return;
    }
    setTimeout(tick, 100);
  };
  tick();
};

const ScrollToTop = () => {
  const location = useLocation();
  const prevPath = useRef(location.pathname);
  const prevGroup = useRef(getGroup(location.pathname));
  const isFirstRun = useRef(true);

  // Track the live scroll offset and write it out before the page goes away.
  useEffect(() => {
    const main = document.querySelector("main");
    if (!main) return undefined;
    let frame = 0;
    const onScroll = () => {
      if (frame || restoring) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        // Read the path fresh — this listener outlives any one route.
        scrollPositions[window.location.pathname] = main.scrollTop;
      });
    };
    main.addEventListener("scroll", onScroll, { passive: true });
    // pagehide covers reload/navigation; visibilitychange catches the mobile
    // case where a tab is killed in the background without either firing.
    window.addEventListener("pagehide", persist);
    document.addEventListener("visibilitychange", persist);
    return () => {
      if (frame) cancelAnimationFrame(frame);
      main.removeEventListener("scroll", onScroll);
      window.removeEventListener("pagehide", persist);
      document.removeEventListener("visibilitychange", persist);
      persist();
    };
  }, []);

  useEffect(() => {
    const main = document.querySelector("main");
    const currentGroup = getGroup(location.pathname);
    const sameGroup = currentGroup === prevGroup.current;

    // First run is a fresh load, not a navigation: put the reader back where
    // they were instead of treating it as a jump to a new page.
    if (isFirstRun.current) {
      isFirstRun.current = false;
      prevPath.current = location.pathname;
      prevGroup.current = currentGroup;
      const target = scrollPositions[location.pathname];
      if (main && target > 0) restoreScroll(main, target);
      return;
    }

    // Save scroll position of previous page
    if (main) {
      scrollPositions[prevPath.current] = main.scrollTop;
    }

    if (sameGroup) {
      // Same group — restore saved scroll or keep position
      requestAnimationFrame(() => {
        if (main && scrollPositions[location.pathname] !== undefined) {
          main.scrollTop = scrollPositions[location.pathname];
        }
      });
    } else {
      // Different group — scroll to top
      if (main) main.scrollTop = 0;
      window.scrollTo(0, 0);
    }

    prevPath.current = location.pathname;
    prevGroup.current = currentGroup;
  }, [location.pathname]);

  return null;
};

export default ScrollToTop;
